import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';

// Pages
import LoginPage from './pages/LoginPage';
import CarListPage from './pages/CarListPage';
import SettingsPage from './pages/SettingsPage';

// Components
import Sidebar from './components/Sidebar';

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  position: relative;
`;

const BurgerButton = styled.button<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 15px;
  left: 15px;
  z-index: 1001;
  background: #000;
  border: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
  }

  span {
    width: 22px;
    height: 2px;
    background: white;
    margin: 2px 0;
    transition: 0.3s;
    transform-origin: center;

    &:nth-child(1) {
      transform: ${props => props.isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      opacity: ${props => props.isOpen ? '0' : '1'};
    }

    &:nth-child(3) {
      transform: ${props => props.isOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'rotate(0)'};
    }
  }
`;

const SidebarOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const ContentContainer = styled.div<{ sidebarOpen: boolean }>`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 60px 20px 20px 20px;
    margin-left: 0;
  }
`;

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Check if user is already logged in on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsLoggedIn(true);
      } catch (err) {
        console.error('Error parsing stored user:', err);
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle login
  const handleLogin = () => {
    setIsLoggedIn(true);
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    setIsLoggedIn(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  if (!isLoggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <AppContainer>
      <BurgerButton isOpen={sidebarOpen} onClick={toggleSidebar}>
        <span></span>
        <span></span>
        <span></span>
      </BurgerButton>
      
      <SidebarOverlay isOpen={sidebarOpen} onClick={closeSidebar} />
      
      <Sidebar 
        onLogout={handleLogout} 
        username={user?.username} 
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />
      
      <ContentContainer sidebarOpen={sidebarOpen}>
        <Routes>
          <Route path="/" element={<Navigate to="/cars" />} />
          <Route path="/cars" element={<CarListPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/cars" />} />
        </Routes>
      </ContentContainer>
    </AppContainer>
  );
}

export default App; 